import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Car, Copy, ExternalLink, Flag, MapPin, Navigation, Phone, Route } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { ADDRESS, MAPS_URL, PHONE, PHONE_HREF } from "@/lib/site";
import { useSeo } from "@/lib/seo";

const GPS = `S 33° 52' 56.4" E 18° 37' 53.7"`;

const DIRECTIONS_TEXT = [
  "Cool Runnings Family Toboggan Park — it takes just under 25 minutes to get here from the Cape Town City Centre (25km).",
  "1. Take the N1 north towards Paarl. Take Exit 23 (R302 Durban Road / Willie van Schoor Drive), which is opposite VW/Ford on the highway.",
  "2. At the top of the off-ramp, turn left into Willie van Schoor Drive.",
  "3. At the first set of traffic lights, turn left into Mispel Road.",
  "4. At the intersection with Carl Cronje Drive, proceed straight into the Cool Runnings entrance, which will lead you to our parking facilities.",
  `GPS: ${GPS}`,
].join("\n");

const STEPS = [
  {
    icon: Car,
    title: "Starting from the city centre",
    text: "It takes just under 25 minutes to get to Cool Runnings from the City Centre — the park lies just 25km away in Tygervalley.",
  },
  {
    icon: Route,
    title: "N1 north & Exit 23",
    text: "Take the N1 north towards Paarl. Take Exit 23 (R302 Durban Road / Willie van Schoor Drive), which is opposite VW/Ford on the highway.",
  },
  {
    icon: ArrowLeft,
    title: "Turn left at the top",
    text: "At the top of the off-ramp, turn left into Willie van Schoor Drive.",
  },
  {
    icon: Navigation,
    title: "First traffic lights left",
    text: "At the first set of traffic lights, turn left into Mispel Road.",
  },
  {
    icon: Flag,
    title: "Arrive & park",
    text: "At the intersection with Carl Cronje Drive, proceed straight into the Cool Runnings entrance, which will lead you to our parking facilities.",
  },
];

export default function DirectionsPage() {
  useSeo({
    title: "Directions & Map | Cool Runnings Cape Town",
    description:
      "Find us in Tygervalley, Cape Town — directions, map, parking and gate info for Cool Runnings Toboggan Family Park.",
    keywords:
      "directions to Cool Runnings, toboggan park location Cape Town, Cool Runnings map, how to get to Cool Runnings, Cool Runnings parking",
    image: "/og/directions.jpg",
  });
  const copyText = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch {
      // Sandboxed iframes / older browsers deny the async clipboard API — fall back to a hidden textarea
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy failed");
        toast.success(message);
      } catch {
        toast.error("Couldn't copy — please note it down instead.");
      }
    }
  };

  const copyGps = () => copyText(GPS, "GPS coordinates copied!");
  const copyDirections = () => copyText(DIRECTIONS_TEXT, "Full directions copied!");

  return (
    <>
      <PageHeader
        goodgirlTitle
        pageName="Directions"
        eyebrow="25km from the city centre"
        title="DIRECTIONS"
        subline="Follow the N1 north, hop off at Exit 23 and roll straight into our parking lot — here is the full route, turn by turn."
        image="/park-launch.jpg"
        action={
          <button
            onClick={copyDirections}
            className="btn-gold !px-6 !py-3 !text-xs"
            data-testid="directions-header-copy-btn"
          >
            <Copy size={14} /> Copy directions
          </button>
        }
      />

      <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6" data-testid="directions-cta-section">
        <div className="card-dark flex flex-wrap items-center justify-between gap-5 p-6">
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f8b818]/20 text-[#c87700]">
              <MapPin size={24} />
            </span>
            <div>
              <p className="font-display text-lg leading-snug">{ADDRESS}</p>
              <p className="mt-1 text-sm font-semibold text-[#8a7b5e]">Free parking right at the entrance.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold !px-5 !py-2.5 !text-xs"
              data-testid="directions-maps-btn"
            >
              Open in Google Maps <ExternalLink size={14} />
            </a>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#241c0d]/15 bg-[#faf5ea] px-5 py-2.5 text-xs font-extrabold uppercase tracking-widest text-[#241c0d] transition-all hover:border-[#f8b818] hover:text-[#c87700]"
              data-testid="directions-call-btn"
            >
              <Phone size={14} /> {PHONE}
            </a>
          </div>
        </div>

        <div
          className="mt-5 overflow-hidden rounded-3xl border border-[#241c0d]/12 bg-white shadow-[0_10px_30px_rgba(36,28,13,0.08)]"
          data-testid="directions-map-card"
        >
          <iframe
            src={`https://www.google.com/maps?q=${encodeURIComponent("Cool Runnings Toboggan Park, Off Carl Cronje Dr, Tygervalley, Cape Town, 7530, South Africa")}&z=16&hl=en&output=embed`}
            title="Google Map showing Toboggan Family Park (Pty) Ltd., t/a Cool Runnings in Tygervalley, Cape Town"
            className="h-[340px] w-full border-0 sm:h-[440px]"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="directions-map-embed"
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="directions-steps-section">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
        >
          <p className="eyebrow">Turn by turn</p>
          <h2 className="mt-3 text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>FROM CAPE TOWN TO THE TRACK</h2>
        </motion.div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                className="card-dark flex flex-col p-6"
                data-testid={`directions-step-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#f8b818]/20 text-[#c87700]">
                    <Icon size={20} />
                  </span>
                  <span className="font-display text-3xl text-[#f8b818]">0{i + 1}</span>
                </div>
                <h3 className="font-display mt-4 text-xl">{step.title}</h3>
                <p className="mt-2 text-sm font-medium leading-relaxed text-[#3d362c]">{step.text}</p>
              </motion.div>
            );
          })}

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: STEPS.length * 0.06 }}
            className="card-dark flex flex-col border-2 border-[#f8b818]/50 p-6"
            data-testid="directions-gps-card"
          >
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#f8b818]/20 text-[#c87700]">
                <MapPin size={20} />
              </span>
              <span className="font-display text-3xl text-[#f8b818]">GPS</span>
            </div>
            <h3 className="font-display mt-4 text-xl">GPS Co-ordinates</h3>
            <p className="mt-2 flex-1 text-sm font-bold leading-relaxed text-[#3d362c]" data-testid="directions-gps-value">
              {GPS}
            </p>
            <button
              onClick={copyGps}
              className="mt-5 inline-flex items-center gap-1.5 self-start rounded-full border-2 border-[#241c0d]/15 bg-[#faf5ea] px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-[#241c0d] transition-all hover:border-[#f8b818] hover:text-[#c87700]"
              data-testid="directions-gps-copy-btn"
            >
              <Copy size={13} /> Copy coordinates
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
          className="mt-12 text-center"
        >
          <button
            onClick={copyDirections}
            className="btn-gold !px-7 !py-3.5 !text-xs"
            data-testid="directions-steps-copy-btn"
          >
            <Copy size={15} /> Copy directions
          </button>
          <p className="mt-3 text-xs font-bold uppercase tracking-widest text-[#8a7b5e]">
            All four turns &amp; the GPS pin — paste it into the family chat
          </p>
        </motion.div>
      </section>
    </>
  );
}
